<template>
  <div role="main">
    <div
      class="version"
      aria-hidden="true"
      aria-disabled="true">
      <div
        role="presentation"
        @dblclick="showThemeTools = true">
        <strong>Build: </strong><span class="text-tt">{{ cleanVersion }}</span>
        <template v-if="showThemeTools">
          &nbsp;&nbsp;
          <strong>Date: </strong> {{ buildDate }}
          &nbsp;&nbsp;
          <template v-if="theme && theme.themeId">
            <strong>Theme:</strong> {{ theme.name }}
            <button
              class="btn btn-link" @click="clearTheme"
            >Reset Theme</button>
          </template>
          <template v-else>
            <small><strong>Default Theme</strong></small>
          </template>
        </template>
      </div>
    </div>
    <spooky-ghost v-if="whatHolidayIsIt() === 'halloween'"></spooky-ghost>
    <festive-lights
      v-if="lodash.includes(
        ['prechristmas', 'christmaseve', 'christmasday', 'boxingday', 'nye'],
        whatHolidayIsIt())">
    </festive-lights>
    <div v-if="err">
      <error-page :err="err" @clearError="err = null"></error-page>
    </div>
    <div class="login-wrapper" id="login-wrapper" v-else>
      <div id="bg"></div>
      <canvas
        id="canv"
        :class="{
          'lighten-canvas': lodash.includes(
            ['prechristmas', 'christmaseve', 'christmasday', 'boxingday',
              'nye'],
            whatHolidayIsIt()),
        }">
      </canvas>
      <div v-if="!loading">
        <transition name="fade">
          <login-card
            v-if="cardVisibility.login"
            class="fade-in-load"
            style="position: absolute; z-index: 100;"
            :darkMode="darkMode"
            :emailOverride="emailOverride"
            :emailProvidedToSSO="emailProvidedToSSO"
            :goToPasswordReset="goToPasswordReset"
            :goToSignup="goToSignup"
            :logoLoginUrl="logoLoginUrl"
            :logoStyle="logoStyle()"
            :themeName="theme.name"
            :topMessage="topMessage"
            :outlookAddin="outlookAddin"
            :chromeExtension="chromeExtension"
            @clearTheme="clearTheme"
            @done="loadApp"
            @emitShowMembershipPortal="$refs.partnerModal.ShowModal"
            @emitLoginWithDefaultSSO="handleLoginWithDefaultSSO">
          </login-card>
        </transition>
        <transition name="fade">
          <password-reset-card
            v-if="cardVisibility.pwReset"
            style="position: absolute; z-index: 100;"
            :goToLogin="goToLogin"
          />
        </transition>
        <transition name="fade">
          <signup-card
            v-if="cardVisibility.signup"
            class="fade-in-load"
            style="position: absolute; z-index: 100;"
            :emailOverride="emailOverride"
            :goToLogin="goToLogin"
            :goToPasswordReset="goToPasswordReset"
            :logoLoginUrl="logoLoginUrl"
            :logoStyle="logoStyle()"
            :themeId="theme.themeId"
            :themeName="theme.name"
            @clearTheme="clearTheme"
            @done="loadApp"
            @emitLoginWithDefaultSSO="handleLoginWithDefaultSSO"
          />
        </transition>
        <transition name="fade">
          <maintenance-card
            v-if="cardVisibility.maintenance"
            class="fade-in-load"
            style="position: absolute; z-index: 100;"
          />
        </transition>
        <announcement />
      </div>
    </div>
    <partner-modal
      :afterLoaded="partnerModalAfterLoaded"
      :entityIdAutoLogin="partnerModalEntityId"
      :loginLoading="loading"
      :mobileView="mobileView"
      @emitError="errChild => err = errChild"
      ref="partnerModal">
    </partner-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import { detect } from 'detect-browser';
import isMobile from 'is-mobile';
import moment from 'moment';
import * as Sentry from '@sentry/vue';
import tinycolor from 'tinycolor2';

import auth from 'assets/js/auth';

import {
  DisableLoginAnimationFetch,
  DisableLoginAnimationPurge,
  DisableLoginAnimationSetTrue,
  ThemeIdFetch,
  ThemeIdPurge,
  ThemeIdSet,
} from 'assets/js/auth/theme';
import { Get, GenerateFreshJWT, Post, urls } from 'assets/js/api';
import {
  RedirectorsFetch,
  RedirectorsSet,
} from 'assets/js/auth/redirect';
import { SESSION_IDP_ENTITY_ID_KEY } from 'assets/js/api/session';
import { THEME_ID } from 'assets/js/api/environment';

import withMobileView from 'assets/js/composables/withMobileView';
import { DARK_MODE_KEY, setDarkMode } from 'assets/js/theme';

import Announcement from 'components/Login/Announcement';
import ErrorPage from 'components/Widgets/ErrorPage';
import FestiveLights from 'components/Login/Holiday/FestiveLights';
import LoginCard from 'components/Login/LoginCard';
import MaintenanceCard from 'components/Login/MaintenanceCard';
import PartnerModal from 'components/Login/PartnerModal';
import PasswordResetCard from 'components/Login/PasswordResetCard';
import SignupCard from 'components/Login/SignupCard';
import SpookyGhost from 'components/Login/Holiday/SpookyGhost';

// Animation related variables.
let elapsedFrames = 0;
const maxFrames = 8000;
const frameCycler = 800;
let frameFlag = false;

const EMAIL_IN_USE_MSG = 'User with this email exists';
const UBC_ENTITY_ID = 'https://authentication.ubc.ca';

export default {
  name: 'q-login',

  components: {
    Announcement,
    ErrorPage,
    FestiveLights,
    LoginCard,
    MaintenanceCard,
    PartnerModal,
    PasswordResetCard,
    SignupCard,
    SpookyGhost,
  },

  setup() {
    const { mobileView } = withMobileView({
      pixelWidthCutoff: 750,
    });
    return {
      mobileView,
    };
  },

  beforeUnmount() {
    // Terminate the animation if it's still going.
    elapsedFrames = maxFrames;

    // Reset the body scroll behaviour.
    document.body.style['overflow-y'] = 'auto';
  },

  computed: {
    cleanVersion() {
      if (!this.version || !this.version.length) return 'No Version';
      return this.version.replace(/"/g, '').substring(0, 8);
    },

    logoLoginHeight() {
      return this.$store.getters.logoLoginHeight;
    },

    logoLoginUrl() {
      return this.$store.getters.logoLoginUrl;
    },

    chromeExtension() {
      return !!(window?.sessionStorage?.getItem('chromeExtension'));
    },

    outlookAddin() {
      return !!(window?.sessionStorage?.getItem('qroutlook'));
    },

    theme() {
      return this.$store.state.theme;
    },
  },

  async created() {
    this.setTitle('Login');

    let createdResolve;
    this.createdPromise = new Promise((resolve) => {
      createdResolve = resolve;
    });

    this.loading = true;

    this.checkUptime();
    const theme = await this.chooseTheme();
    this.$store.dispatch('SetTheme', theme);
    if (theme && theme.theme_id) ThemeIdSet(theme.theme_id);

    if (theme) {
      this.setTitle(`Sign in to ${theme.name}`);
    }

    let darkMode = false;
    if (window.localStorage) {
      darkMode = window.localStorage.getItem(this.DARK_MODE_KEY) === '1';
    }
    await this.setDarkMode(darkMode);

    createdResolve();
  },

  data() {
    return {
      DARK_MODE_KEY,

      buildDate: moment(process.env.BUILD_DATE).format('llll'),
      cardVisibility: {
        login: true,
        maintenance: false,
        pwReset: false,
        signup: false,
      },
      createdPromise: null,
      darkMode: false,
      emailOverride: '',
      emailProvidedToSSO: null, // Entity object where email was sent
      err: null,
      hideMenu: false,
      loading: false,
      partnerModalAfterLoaded: null,
      partnerModalEntityId: '',
      showMaintenance: true,
      showThemeTools: false,
      topMessage: '',
      version: String(process.env.VERSION),
      windowWidth: window.innerWidth,
    };
  },

  methods: {
    // attemptJWTLogin attempts the use the JWT passed in the query to
    // pull down SELF; if it succeeds, it stores the login information and
    // goes to the dashboard.
    async attemptJWTLogin(jwt, expires) {
      try {
        await auth.LoginWithToken(jwt, expires);
      } catch (err) {
        await this.clearAllLoginData();
        this.shipNotification(
          `JWT token login failed: ${err}`, 'danger', 6000);
        return err;
      }

      // If we're logging in through SAML, persist the login.
      if (_.get(this, '$route.query.idp_entity_id', false)) {
        auth.TokenStore({ token: jwt, expires });
      }

      try {
        const tokenNew = await GenerateFreshJWT(expires);
        await auth.LoginWithToken(tokenNew.token, tokenNew.expires);

        try {
          // Attempt to post back to Google Calendar.
          const EXTENSION_ID = 'phaojmikmonhckadmafmdlcndimakihd';
          const DEV_EXTENSION_ID = 'bcddkeoedncagcpoeilcjahcplekmgli';
          const accessUrl = window.location.href;
          if (window.chrome) {
            window.chrome.runtime.sendMessage(
              EXTENSION_ID,
              { SSOLogin: accessUrl },
              (response) => {
                if (response.receivedByExtension) {
                  window.close(); // Close myself.
                }
              });

            // Dev
            window.chrome.runtime.sendMessage(
              DEV_EXTENSION_ID,
              { SSOLogin: accessUrl },
              (response) => {
                if (response.receivedByExtension) {
                  window.close(); // Close myself.
                }
              });
          }
        } catch (err) {
          console.error('sendMessage error when trying to access Google ' +
            'Calendar', err);
        }

        try {
          // If we are not in an iframe (dialog) then push url to host.
          if (this.outlookAddin && window === window.parent) {
            const officeUrl = encodeURIComponent(window.location.href);
            await this.staticScriptAsyncLoad({
              name: 'MSOfficeAPI',
              urlPathRemote:
                'https://appsforoffice.microsoft.com/lib/1/hosted/office.js',
            });

            if (window.Office) {
              let resolver;
              const prms = new Promise((resolve) => { resolver = resolve; });
              window.Office.onReady(() => {
                // Take our location try redirecting back.
                console.debug('Office is ready');
                const redirectUrl = JSON.stringify({
                  url: btoa(officeUrl),
                });
                window.Office.context.ui.messageParent(redirectUrl);
                console.debug('Resolving Office Promise');
                resolver();
              });
              await prms;
            }
          }
        } catch (err) {
          console.error('Office Error', err);
        }

        this.loadApp();
      } catch (err) {
        await this.clearAllLoginData();
        this.shipNotification(`JWT token login failed: ${err}`, 'danger', 6000);
      }

      return true;
    },

    // backgroundMagic makes pretty lines fly around in the background.
    backgroundMagic() {
      // Upon loading the login screen, reset any stored personal information
      // in the store that may have been from a previous use logging in.
      // TODO Remove? Appears to have been moved to mainmenu.vue.
      // _.forEach(ModuleNames, name => this.$store.dispatch(`${name}Reset`));

      // Pretty color bezier thing
      // Inspired by http://codepen.io/tmrDevelops/pen/MwzpQK
      window.requestAnimFrame = (thing) =>
        window.requestAnimationFrame(thing) ||
          window.webkitRequestAnimationFrame(thing) ||
          window.mozRequestAnimationFrame(thing) ||
          window.oRequestAnimationFrame(thing) ||
          window.msRequestAnimationFrame(thing);

      let c = {};
      if (document.getElementById('canv')) {
        c = document.getElementById('canv');
      }
      let $;
      if (c && c.getContext && (typeof (c.getContext) === 'function') &&
          c.getContext('2d')) {
        $ = c.getContext('2d');
      }
      const w = window.innerWidth;
      c.width = w;
      const h = window.innerHeight;
      c.height = h;
      let arr = [];
      let hue = -120;

      const bg = document.getElementById('bg');
      if (bg) {
        bg.style.width = '100vw';
        bg.style.height = '100vh';
      }

      // If we have a defined theme, i.e this.theme.name is defined,
      // use the hue value from the theme's primary colour.
      if (this.theme && this.theme.name && this.theme.primaryColor) {
        hue = tinycolor(this.theme.primaryColor).toHsl().h;
      }
      let u = hue;

      let forceDisableAnimation = false;

      const disabledAnimation = this.$route.query.disableAnimation === 1;

      if (disabledAnimation) {
        forceDisableAnimation = true;
        // If possible, remember to disable next time as well.
        DisableLoginAnimationSetTrue();
      }

      // Check local storage too.
      forceDisableAnimation = DisableLoginAnimationFetch(forceDisableAnimation);

      // Ability to re-enable animations if needed.
      if (this.$route.query.disableAnimation === '0') {
        forceDisableAnimation = false;
        DisableLoginAnimationPurge();
      }

      const browser = detect();
      const noAnimationBrowsers = ['edge', 'ie'];

      // Check if we have animation capability.
      const capableBrowser =
          !!window.requestAnimationFrame ||
          !!window.webkitRequestAnimationFrame ||
          !!window.mozRequestAnimationFrame ||
          !!window.oRequestAnimationFrame ||
          !!window.msRequestAnimationFrame;

      if (this.outlookAddin) {
        c.className = 'white';
        return;
      }

      if (isMobile() ||
            forceDisableAnimation ||
            !browser ||
            !browser.name ||
            !capableBrowser ||
            noAnimationBrowsers.indexOf(browser.name) !== -1) {
        // Set a nice blue background and skip the animation on mobile or IE.
        c.className = 'gradient';
        this.loading = false;
        return;
      }

      if ($ && c) {
        const set = () => {
          arr.push({
            x1: w,
            y1: h,
            _x1: w - (Math.random() * w),
            _y1: h - (Math.random() * h),
            _x2: w - (Math.random() * w),
            _y2: h - (Math.random() * h),
            x2: -w + (Math.random() * w),
            y2: -h + (Math.random() * h),
            rot: Math.random() * 180,
            a1: Math.random() * 20,
            a2: Math.random() * 10,
            a3: Math.random() * 500,
          });
        };

        // Initialize the animation lines.
        const ready = () => {
          arr = [];
          let i = 0;
          for (; i < 20; i += 1) {
            set();
          }
        };

        window.addEventListener('resize', () => {
          if ($) {
            $.clearRect(0, 0, window.innerWidth, window.innerHeight);
          }
          elapsedFrames = 0;
          frameFlag = false;
          if (c) {
            c.width = window.innerWidth;
            c.height = window.innerHeight;
          }
          ready();
          u = hue;
          $.fillStyle = '#FFFFFF';
          if (this.darkMode || document.body.classList.contains('dark-mode')) {
            $.fillStyle = '#222222';
          }
          $.fillRect(0, 0, c.width, c.height);
        }, false);

        // Initialize and clear any old values/canvas.
        $.clearRect(0, 0, window.innerWidth, window.innerHeight);
        elapsedFrames = 0;
        frameFlag = false;

        $.fillStyle = '#FFFFFF';
        if (this.darkMode || document.body.classList.contains('dark-mode')) {
          $.fillStyle = '#222222';
        }
        $.fillRect(0, 0, w, h);
        $.globalCompositeOperation = 'source-over';

        // Alter and draw the animation lines.
        const pretty = () => {
          // Every frameCycler many frames, make the change in hue reverse
          // itself.
          let toSubtract = -0.10;
          if (elapsedFrames > 0 && elapsedFrames % frameCycler === 0) {
            frameFlag = !frameFlag;
          }
          if (frameFlag) toSubtract = -toSubtract;

          u -= toSubtract;
          _.forEach(arr, (val) => {
            const b = val;
            b['_x1'] *= Math.sin(b.a1 -= 0.001);
            b['_y1'] *= Math.cos(b.a1);
            b['_x2'] -= Math.sin(b.a2 += 0.001);
            b['_y1'] += Math.cos(b.a2);
            b.x1 -= Math.sin(b.a3 += 0.001);
            b.y1 += Math.cos(b.a3);
            b.x2 -= Math.sin(b.a3 -= 0.001);
            b.y2 += Math.cos(b.a3);
            $.save();
            $.globalAlpha = 0.03;
            $.beginPath();
            let avoidRed = '75%';
            if (u < 25.0 && u > -55.0) avoidRed = '30%';
            $.strokeStyle = `hsla(${u}, ${avoidRed}, 60%, 0.5)`;
            $.moveTo(b.x1, b.y1);
            $.bezierCurveTo(
              b['_x1'], b['_y1'], b['_x2'], b['_y2'], b.x2, b.y2);
            $.stroke();
            $.restore();
          });

          // Terminate recursion if we've animated enough.
          if (elapsedFrames < maxFrames) window.requestAnimFrame(pretty);
          else return;

          elapsedFrames += 1;
        };

        ready();
        pretty();
      }
    },

    // checkUptime does a check for whether the API is running and if not shows
    // a maintenance screen.
    async checkUptime() {
      try {
        await Get(urls.PING);
        console.info('Successfully received ping from QReserve API.');
        return true;
      } catch (err) {
        console.warn('Unable to ping QReserve API.');
        if (!this.cardVisibility.maintenance) {
          this.goToMaintenance();
        }
        return false;
      }
    },

    // chooseTheme returns the theme that should be used based on the
    // store state and the query parameters. Returns null if we should be
    // using the QReserve default theme.
    async chooseTheme() {
      // If there's a theme id in the query params, get it and use it.
      let themeId = this.$route.query.theme;
      let theme;

      // If not explicitly, do we have one in session storage?
      if (!themeId) {
        themeId = ThemeIdFetch();
      }

      // Give the user the ability to purge the theme ID and use the default
      // if they want it.
      if (this.$route.query.disableTheme) {
        themeId = null;
        ThemeIdPurge();
      }

      // If not explicitly asked for, check if we have a default theme set.
      if (!themeId) {
        themeId = THEME_ID;
      }

      if (themeId) {
        try {
          theme = await Get(urls.THEME(themeId));
          return theme.data;
        } catch (err) {
          return null;
        }
      }

      // If there is a defined theme in the store, use that.
      if (this.theme && this.theme.name) return this.theme;

      return null;
    },

    // clearAllLoginData strips out cookies, vuex, and session storage so
    // that memberships is in a fresh state.
    async clearAllLoginData() {
      await this.signOut();
    },

    clearTheme() {
      ThemeIdPurge();
      window.location.reload();
    },

    // goToLogin switches to the login screen. It is to be
    // passed to the child component for navigation.
    goToLogin(showNeedEmailVal) {
      this.topMessage = '';
      if (showNeedEmailVal) {
        this.needEmailValidationModal();
        this.topMessage = 'Please check your email to validate your ' +
          'account before signing in for the first time.';
      }

      this.cardVisibility.login = true;
      this.cardVisibility.pwReset = false;
      this.cardVisibility.signup = false;
      this.cardVisibility.maintenance = false;
    },

    goToMaintenance() {
      this.cardVisibility.login = false;
      this.cardVisibility.pwReset = false;
      this.cardVisibility.signup = false;
      this.cardVisibility.maintenance = true;

      // Check once every 65 +/ 15 seconds if we're back up.
      const recheck = window.setInterval(async () => {
        const backUp = await this.checkUptime();
        if (backUp) {
          // If we are back up just do a full refresh.
          window.location.reload();
        }
      }, 50000 + Math.random() * 30000);
    },

    // goToPasswordReset switches to the password reset
    // screen. It is to be passed to the child component for
    // navigation.
    goToPasswordReset() {
      this.cardVisibility.login = false;
      this.cardVisibility.pwReset = true;
      this.cardVisibility.signup = false;
      this.cardVisibility.maintenance = false;
    },

    // goToSignup switches to the signup screen. It is to be passed to the
    // child component for navigation.
    goToSignup() {
      this.cardVisibility.login = false;
      this.cardVisibility.pwReset = false;
      this.cardVisibility.signup = true;
      this.cardVisibility.maintenance = false;
    },

    // handleLoginWithDefaultSSO handles loggin into an SSO with an IDP in the
    // form of { entity_id: String, ... }.
    async handleLoginWithDefaultSSO(idp) {
      let externalResolve;
      const viewMounted = new Promise((resolve) => {
        externalResolve = resolve;
      });
      this.partnerModalAfterLoaded = externalResolve;
      this.partnerModalEntityId = idp.entity_id;

      // Props update only at the next tick so we have to call this then.
      await this.$nextTick();
      this.$refs.partnerModal.ShowModal();
      await viewMounted;
      await this.$refs.partnerModal.doExternalLogin();

      // Delay here so we don't show the login components while the page
      // changes.
      if (!this.err) {
        await this.sleep(10000);
      }
    },

    // joinFederation prompts the user to indicate that when they log in with
    // their QReserve account, their account will be automatically linked
    // with the affiliate they just logged in from.
    async joinFederation() {
      this.emailOverride = this.$route.query.idp_email;
      try {
        await this.$swal({
          title: 'Associate QReserve Account with ' +
              `${this.$route.query.idp_entity_name}`,
          text: `${this.$route.query.idp_entity_name} wants to enable ` +
              'their login system for your QReserve account. To proceed, ' +
              'simply log in with your current QReserve account. After ' +
              'logging in, you will be allowed to log into future sessions ' +
              'using their sign on service.',
          type: 'success',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: false,
          confirmButtonText: 'Continue',
        });
      } catch (err) {
        // sweetalert cancel
      }
    },

    // loadApp loads the application after the user has logged in.
    loadApp() {
      const {
        redirectPage,
        redirectParams,
        redirectQuery,
      } = RedirectorsFetch();

      if (redirectPage) {
        // If a redirect was passed in, try going there.
        let params = {};
        if (redirectParams) {
          try {
            params = JSON.parse(decodeURI(redirectParams));
          } catch (e) {
            try {
              params = JSON.parse(redirectParams);
            } catch (e2) {
              console.warn('Unable to parse redirection parameters', e, e2);
            }
          }
        }

        const newLocation = {
          name: redirectPage,
          params,
          query: redirectQuery,
        };
        this.$router.push(newLocation);
      } else {
        // Otherwise, navigate to the dashboard.
        const dashboardQuery = {};
        if (this.$route.query.prompt_password) {
          dashboardQuery.prompt_password = 1;
        }
        this.$router.Navigate.Dashboard(dashboardQuery);
      }
    },

    logoStyle() {
      if (this.logoLoginHeight) {
        return { height: this.logoLoginHeight };
      }
      return {};
    },

    // needEmailForFederationMembership is for SAML sign on partners who provide
    // a persistent ID, which we will match up with an email address that the
    // user gives us so we can verify the user, add email support, and ensure
    // that the user has a backup means to recover their account.
    async needEmailForFederationMembership({
      emailFromUser,
      entityId,
      entityName,
      membershipId,
    }) {
      let emailEntered;
      let errEmailPrompt;
      try {
        await this.$swal({
          title: 'Email Verification for Sign-on Partner',
          input: 'email',
          inputValue: emailFromUser === '1' ? '' : emailFromUser,
          inputPlaceholder: 'Enter your email address',
          html: `Your account for single sign-on with the organization
            ${entityName} is almost registered! <br><br>We need a
            <strong>verified email</strong> so that we have a way to reach you.
            <br><br>Please enter your email below and a
            verification email will be sent to you. After verifying, you
            will be able to log in.
          `,
          animation: true,
          showLoaderOnConfirm: true,
          confirmButtonText: 'Send verification email',
          allowOutsideClick: false,
          preConfirm: async (email) => {
            emailEntered = email;
            try {
              await Post(urls.FEDERATION_MEMBERSHIP_SET_EMAIL, {
                email,
                federation_membership_id: membershipId,
              });
            } catch (err) {
              errEmailPrompt = err;
            }
          },
        });
      } catch (err) {
        // Sweet alert cancel
      }

      if (errEmailPrompt && !_.includes(errEmailPrompt, EMAIL_IN_USE_MSG)) {
        this.$swal({
          type: 'error',
          title: 'Single Sign-on Email Error',
          text: 'Oh no! We were unable to set your email address correctly.' +
            ` (server error: ${errEmailPrompt})`,
          animation: true,
        });
      } else {
        await this.$swal({
          type: 'success',
          title: 'Verify your email address to finish sign up!',
          text: `Please verify the email we just sent to complete account
            creation. Once verified, click "Sign in with partner" to sign in to
            your newly created account.`,
          animation: true,
        });
        this.emailProvidedToSSO = {
          email: emailEntered,
          name: entityName,
          names: [entityName],
          entity_id: entityId,
        };
      }
    },

    // needEmailValidationModal pops up a modal showing that the user needs
    // to verify their email address.
    needEmailValidationModal(extraText) {
      let txt = 'Your account has been created. Please check your email ' +
          'for instructions on verifying your account prior to logging in.';
      if (extraText) txt = `${txt} ${extraText}`;
      this.$swal({
        type: 'success',
        title: 'QReserve Account Created',
        text: txt,
        animation: true,
      });
    },

    async setDarkMode(darkMode) {
      this.darkMode = darkMode;
      await setDarkMode(this, darkMode);
    },

    // specialUBCVerificationEmailModal is a modal to display to new UBC users
    // informing them that they need to verify that their email address is
    // correct in their profile.
    async specialUBCVerificationEmailModal() {
      const txt = 'Your account has been created and you have been ' +
          'automatically logged in. Please verify that the email address ' +
          'in your profile matches your current email address, as your ' +
          'single sign-on provider may have provided an outdated email.';
      await this.$swal({
        type: 'success',
        title: 'QReserve Account Created',
        text: txt,
        animation: true,
      });
      if (this.$store.state.auth?.authUser) {
        this.$router.Navigate.User(this.$store.state.auth?.authUser?.user_id);
      }
    },
  },

  async mounted() {
    await this.createdPromise;

    // Set the scroll overflow behaviour to hide the right bar.
    document.body.style['overflow-y'] = 'hidden';
    this.backgroundMagic();

    // If the URL has ?page=passwordreset in it then jump to the password
    // reset. This makes it easy to send people straight to this page.
    if ((
      this.$route.query.page &&
        this.$route.query.page === 'passwordreset') ||
      this.$route.meta.passwordReset) {
      this.goToPasswordReset();
      this.loading = false;
      return;
    }

    // If the URL has ?page=signup in it then jump to the password
    // reset. This makes it easy to send people straight to this page.
    if (this.$route.query.page &&
        this.$route.query.page === 'signup') {
      // Optionally specify the email to pre-fill.
      if (this.$route.query.email) {
        this.emailOverride = this.$route.query.email;
      }
      this.goToSignup();
      this.loading = false;
      return;
    }

    // If we have a redirect then pull it out of the URL and store locally
    if (this.$route.query.redirect) {
      RedirectorsSet(this.$route.query);
    }

    // Display the swal for UBC prior to doing the login for them, letting
    // the app log in in the background while they read it.
    if (_.get(this, '$route.query.new_user') &&
      _.get(this, '$route.query.idp_entity_id') === UBC_ENTITY_ID) {
      this.specialUBCVerificationEmailModal();
    }

    // Attempt to get the cookie from the store, or to pull the token
    // from the query parameters. If the legacy cookie is stored, upgrade
    // it to local storage and delete it. Purge all expired stored sessions.
    auth.TokenPurgeIfExpired();

    let jwt = _.get(this, '$route.query.jwt', undefined);
    let expiry = _.get(this, '$route.query.expires', undefined);

    // Check to see if the token is stored in an anchor link.
    if (_.isUndefined(jwt) && _.isUndefined(expiry)) {
      const hashString = window.location.hash.substring(1);
      if (hashString) {
        try {
          const token = JSON.parse(
            decodeURIComponent(hashString).replace(/\+/g, ' '));
          jwt = token.token;
          expiry = token.expires;
        } catch (err) {
          console.warn('Malformed anchor token');
        }
      }
    }

    // Attempt to fetch the jwt and expiry from the local storage.
    let stored;
    if (_.isUndefined(jwt) && _.isUndefined(expiry)) {
      stored = auth.TokenFetch();
    }
    if (stored) {
      jwt = stored.token;
      expiry = stored.expires;
    }

    // We're accessing QReserve through this IDP, which is necessary to
    // pull down a redirector for on our way out. Store it for the session.
    if (this.$route.query.idp_entity_id && !this.$route.query.need_email) {
      sessionStorage.setItem(SESSION_IDP_ENTITY_ID_KEY,
        this.$route.query.idp_entity_id);
    }

    // We have an IDP membership ID, meaning that we've logged in from an
    // IDP but that this email address was already within QReserve.
    //
    // DEPRECATED and tagged for removal.
    //
    // if (this.$route.query.idp_membership &&
    //   this.$route.query.need_association) {
    //   await this.joinFederation();
    // }

    // We have an IDP entity name and nothing else. Attempt to log in using
    // that specific IDP.
    if (this.$route.query.idp_entity_name &&
      !this.$route.query.idp_membership &&
      !this.$route.query.need_email) {
      let externalResolve;
      const viewMounted = new Promise((resolve) => {
        externalResolve = resolve;
      });
      this.partnerModalAfterLoaded = externalResolve;
      this.partnerModalEntityId = this.$route.query.idp_entity_name;

      // Props update only at the next tick so we have to call this then.
      this.$nextTick(async () => {
        this.$refs.partnerModal.ShowModal();
        await viewMounted;
        await this.$refs.partnerModal.doExternalLogin();
      });

      // Delay here so we don't show the login components while the page
      // changes.
      if (!this.err) {
        await this.sleep(10000);
      }
    }

    // The user needs to make sure that the IDP given email is the correct one,
    // if one was given it at all.
    if (this.$route.query.idp_membership &&
      this.$route.query.need_email &&
      this.$route.query.idp_entity_name &&
      this.$route.query.idp_entity_id) {
      await this.needEmailForFederationMembership({
        emailFromUser: this.$route.query.need_email,
        entityId: this.$route.query.idp_entity_id,
        entityName: this.$route.query.idp_entity_name,
        membershipId: this.$route.query.idp_membership,
      });
    }

    // Email verification message for some SAML IDP login requests.
    if (this.$route.query.need_validation) {
      this.needEmailValidationModal('After verifying, you will be able ' +
        'to sign in through your partner organization.');
    }

    // Login with JWT!
    if (jwt && expiry) {
      const errLogin = await this.attemptJWTLogin(jwt, expiry);

      // Don't show the login components while we're loading the dashboard.
      if (!this.err && !(errLogin instanceof Error)) {
        await this.sleep(10000);
      }
    }

    // There is no stored token, so the last session was not persisted. Be
    // sure to clear the auth cache here in case the user went back to the
    // login page through the router instead of logging out. However, do NOT
    // do this is we're expecting to prepopulate some portions of the page
    // based on URL parameters.
    const hasExpectedPropopulation = !!this.$route.query.idp_membership ||
      !!this.$route.query.idp_entity_name ||
      !!this.$route.query.page;
    if (!jwt && !hasExpectedPropopulation) {
      await this.clearAllLoginData();
    }

    this.loading = false;
  },

  watch: {
    // Display logout message.
    '$store.state.auth.logoutMessage': {
      handler: function handle(val) {
        if (val) {
          this.$swal({
            type: 'warning',
            title: 'You have been logged out',
            text: this.$store.state.auth.logoutMessage,
            animation: true,
          });

          this.$store.dispatch('SetLogoutMessage', false);
        }
      },
      immediate: true,
    },
    // Error handling watcher.
    err: {
      handler: function handle(err) {
        Sentry.captureException(err);
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
  /* Animations */
  .slide-up-enter, .slide-up-leave-to  {
    transform: translateY(-800%);
  }

  .slide-up-enter-active, .slide-up-leave-active {
    transition: all 0.3s ease;
  }

  .slide-down-enter, .slide-down-leave-to  {
    transform: translateY(400%);
  }

  .slide-down-enter-active, .slide-down-leave-active {
    transition: all 0.3s ease;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s ease-in;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  .fade-in-load {
    animation: fade-in-load 1.0s;
  }

  @keyframes fade-in-load {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  /* Classes */
  .login-wrapper {
    height: 100%;

    text-align: center;
    align-content: center;
    align-items: center;
  }

  .version {
    position: absolute;
    font-size: 1.33rem;
    background: rgba(0,0,0,0);
    text-align: right;
    font-size: 0.7em;
    color: rgba(0,0,0,0.3);
    padding-right: 1em;
    bottom: 0;
    right: 0;
    button {
      padding: 0;
      color: rgba(0,0,0,0.3);
      font-size: 0.8em;
      font-weight: bold;
    }
  }
  .white {
    background: white;
  }

  .gradient {
    background: var(--primary-color, #d3f1ff); /* Old Browsers */
    background: linear-gradient(to bottom right,
      var(--primary-color, #1e394c), var(--secondary-color, #2879b2));
  }

  #bg {
    display: none; /* default to none */
  }

  .lighten-canvas {
    pointer-events: none;
    opacity: 33%;
  }
</style>
